import React, { useState, useEffect } from 'react';
import { Role } from '../../../../../types/cms.types';
import { AdminWithRoles, CreateAdminRequest, UpdateAdminRequest } from '../../../../../service/supabase/AdminManagementService';
import Input from '../../../../components/common/Input';
import Button from '../../../../components/common/Button';
import './CpnAdminForm.css';

interface CpnAdminFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: CreateAdminRequest | UpdateAdminRequest) => void;
    admin?: AdminWithRoles | null;
    availableRoles: Role[];
    formType: 'create' | 'edit';
    loading: boolean;
    error?: string | null;
}

const CpnAdminForm: React.FC<CpnAdminFormProps> = ({
    isOpen,
    onClose,
    onSubmit,
    admin,
    availableRoles,
    formType,
    loading,
    error
}) => {
    const [formData, setFormData] = useState({
        full_name: '',
        user_name: '',
        pass_word: '',
        confirmPassword: '',
        role_ids: [] as string[]
    });

    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (isOpen) {
            if (formType === 'edit' && admin) {
                setFormData({
                    full_name: admin.full_name || '',
                    user_name: admin.user_name || '',
                    pass_word: '',
                    confirmPassword: '',
                    role_ids: admin.roles?.map(role => role.id).filter(Boolean) as string[] || []
                });
            } else {
                setFormData({
                    full_name: '',
                    user_name: '',
                    pass_word: '',
                    confirmPassword: '',
                    role_ids: []
                });
            }
            setValidationErrors({});
        }
    }, [isOpen, formType, admin]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear validation error when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleRoleToggle = (roleId: string) => {
        setFormData(prev => ({
            ...prev,
            role_ids: prev.role_ids.includes(roleId)
                ? prev.role_ids.filter(id => id !== roleId)
                : [...prev.role_ids, roleId]
        }));
    };

    const validateForm = (): boolean => {
        const errors: { [key: string]: string } = {};

        if (!formData.full_name.trim()) {
            errors.full_name = 'Họ và tên không được để trống';
        }

        if (!formData.user_name.trim()) {
            errors.user_name = 'Tên đăng nhập không được để trống';
        } else if (formData.user_name.length < 3) {
            errors.user_name = 'Tên đăng nhập phải có ít nhất 3 ký tự';
        }

        if (formType === 'create') {
            if (!formData.pass_word.trim()) {
                errors.pass_word = 'Mật khẩu không được để trống';
            } else if (formData.pass_word.length < 6) {
                errors.pass_word = 'Mật khẩu phải có ít nhất 6 ký tự';
            }
        } else {
            // For edit, password is optional but if provided, must be valid
            if (formData.pass_word.trim() && formData.pass_word.length < 6) {
                errors.pass_word = 'Mật khẩu phải có ít nhất 6 ký tự';
            }
        }

        if (formData.pass_word && formData.pass_word !== formData.confirmPassword) {
            errors.confirmPassword = 'Xác nhận mật khẩu không khớp';
        }

        if (formData.role_ids.length === 0) {
            errors.role_ids = 'Vui lòng chọn ít nhất một vai trò';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const submitData = {
            full_name: formData.full_name.trim(),
            user_name: formData.user_name.trim(),
            pass_word: formData.pass_word.trim(),
            role_ids: formData.role_ids
        };

        if (formType === 'edit' && admin) {
            onSubmit({
                ...submitData,
                id: admin.id
            } as UpdateAdminRequest);
        } else {
            onSubmit(submitData as CreateAdminRequest);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="admin-form-overlay">
            <div className="admin-form-dialog">
                <div className="admin-form-header">
                    <h3>{formType === 'create' ? 'Tạo quản trị viên mới' : 'Chỉnh sửa quản trị viên'}</h3>
                    <button
                        className="close-button"
                        onClick={onClose}
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="admin-form-content">
                    <div className="admin-form-layout">
                        {/* Left Column - Basic Info */}
                        <div className="admin-form-left-column">
                            <div className="form-group">
                                <label htmlFor="full_name">Họ và tên *</label>
                                <Input
                                    value={formData.full_name}
                                    onChange={(value) => handleInputChange('full_name', value)}
                                    placeholder="Nhập họ và tên"
                                    disabled={loading}
                                />
                                {validationErrors.full_name && (
                                    <span className="error-message">{validationErrors.full_name}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="user_name">Tên đăng nhập *</label>
                                <Input
                                    value={formData.user_name}
                                    onChange={(value) => handleInputChange('user_name', value)}
                                    placeholder="Nhập tên đăng nhập"
                                    disabled={loading}
                                />
                                {validationErrors.user_name && (
                                    <span className="error-message">{validationErrors.user_name}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="pass_word">
                                    Mật khẩu {formType === 'create' ? '*' : '(để trống nếu không đổi)'}
                                </label>
                                <Input
                                    type="password"
                                    value={formData.pass_word}
                                    onChange={(value) => handleInputChange('pass_word', value)}
                                    placeholder={formType === 'create' ? 'Nhập mật khẩu' : 'Nhập mật khẩu mới'}
                                    disabled={loading}
                                />
                                {validationErrors.pass_word && (
                                    <span className="error-message">{validationErrors.pass_word}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Xác nhận mật khẩu</label>
                                <Input
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={(value) => handleInputChange('confirmPassword', value)}
                                    placeholder="Nhập lại mật khẩu"
                                    disabled={loading}
                                />
                                {validationErrors.confirmPassword && (
                                    <span className="error-message">{validationErrors.confirmPassword}</span>
                                )}
                            </div>

                            {/* Form Actions - Moved to left column */}
                            <div className="admin-form-actions">
                                <Button
                                    variant="secondary"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    Hủy
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={loading}
                                >
                                    {loading ? 'Đang xử lý...' : (formType === 'create' ? 'Tạo mới' : 'Cập nhật')}
                                </Button>
                            </div>
                        </div>

                        {/* Right Column - Role Selection */}
                        <div className="admin-form-right-column">
                            <div className="form-group">
                                <label>Vai trò *</label>
                                <div className="roles-selection">
                                    {availableRoles.map(role => (
                                        role.id && (
                                            <div key={role.id} className="role-item">
                                                <input
                                                    type="checkbox"
                                                    id={`role-${role.id}`}
                                                    checked={formData.role_ids.includes(role.id)}
                                                    onChange={() => handleRoleToggle(role.id!)}
                                                    disabled={loading}
                                                />
                                                <label htmlFor={`role-${role.id}`} className="role-label">
                                                    <span className="role-name">{role.name}</span>
                                                    {role.description && (
                                                        <span className="role-description">{role.description}</span>
                                                    )}
                                                </label>
                                            </div>
                                        )
                                    ))}
                                </div>
                                {validationErrors.role_ids && (
                                    <span className="error-message">{validationErrors.role_ids}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default CpnAdminForm;
